/* eslint-disable */
// @ts-nocheck
var R = require('ramda');
import {
  AMP_KEY,
  INTERACTION_TYPE_DEFAULT,
  INTERACTION_PERIOD_DEFAULT,
  QUERY_BUILDER_TYPES,
} from './utils/journeyConstants';
import { convertDevicesToLucene, convertLuceneToDevices } from './utils/journeyHelpers';
import { STANDARD_EVENT_NAMES } from '../../../common/support/eo_mapping';

const QUERY_BUILDER_TYPES_LIST = Object.keys(QUERY_BUILDER_TYPES);

import type { Filter, RuleQueryData, CompletedEventFilter, BuilderData, ViewDismissFilter } from '../../types/journeyTypes';
// From `audience-rule-service`, these are the available fields:
// [ ] app.session.count
// [ ] country
// [ ] subdivision
// [ ] city
// [ ] desktop.web.session.count
// [ ] event
// [ ] (custom|standard)_event.EACH_EVENT.(count|revenue|day_last_seen)
// [ ] link.data.
// [ ] mobile.web.session.count
// [ ] os
// [ ] device_type
// [ ] platform
// [ ] referrer
// [ ] referrer.host
// [ ] referrer.path_contains
// [ ] referrer.protocol
// [ ] referrer.query
// [ ] initial_referrer
// [ ] initial_referrer.query
// [ ] web.session.count
// [ ] metadata

// The current fields that are in use with Journeys Dashboard are:
// [X] (custom|standard)_event.EACH_EVENT.(count|revenue|day_last_seen)
// [X] app.installed
// [X] app.session.count
// [X] country
// [X] subdivision
// [X] city
// [X] event
// [X] os
// [X] device_type
// [X] platform
// [X] referrer
// [X] referrer.path_contains
// [X] referrer.query
// [X] initial_referrer
// [X] initial_referrer.path_contains
// [X] initial_referrer.query
// [X] web.session.count
// [X] metadata

// this should be a class, but our unit tests cannot handle that yet
function QueryBuilder(data) {
  this._data = QueryBuilder.createEmptyData();

  if (typeof data === 'string') {
    this.setQuery(data);
  } else if (typeof data === 'object') {
    this.setData(data);
  }
}

function createEmptyData() {
  return {
    devices: {
      ios: [],
      android: [],
    },
    platform: [],
    event: [],
    filters: [],
  };
}

function doesFilterIncludeZero(filter: Filter): boolean {
  if (filter.operator === QueryBuilder.VALUE_WITHIN) {
    return false;
  }
  return (
    // $FlowFixMe needs type refinement for Filter
    filter.operator === QueryBuilder.VALUE_LESS_THAN || filter.range[0] === 0
  );
}

function buildRangeForFilter(filter: CompletedEventFilter): string {
  switch (filter.operator) {
    case QueryBuilder.VALUE_GREATER_THAN:
      // $FlowFixMe needs type refinement for Filter
      return `[${filter.range[0]} TO *]`;
    case QueryBuilder.VALUE_LESS_THAN:
      // $FlowFixMe needs type refinement for Filter
      return `[* TO ${filter.range[0]}]`;
    case QueryBuilder.VALUE_EQUALS:
      // $FlowFixMe needs type refinement for Filter
      return `[${filter.range[0]} TO ${filter.range[0]}]`;
    case QueryBuilder.VALUE_BETWEEN:
      // $FlowFixMe needs type refinement for Filter
      return `[${filter.range[0]} TO ${filter.range[1]}]`;
    case QueryBuilder.VALUE_WITHIN:
      // $FlowFixMe needs type refinement for Filter
      return `[1 TO ${filter.range[0]}]`;
    case QueryBuilder.NO_BOUNDS:
      return `[* TO *]`;
    default:
      return '';
  }
}

function escape(input) {
  return input.replace(/[-[\]{}()*+?:=/&.,\\^$|#!\s]/g, '\\$&');
}

function unescape(input) {
  return input.replace(/\\/g, '');
}

function escapeOperators(input) {
  return input.replace(/\b(^OR$|^AND$|^NOT$)\b/g, '"$&"');
}

// Converts query object into lucene query string
function buildQuery(data: RuleQueryData): string {
  var queryList = [];
  data = data || {};

  // devices
  const luceneDeviceQuery = convertDevicesToLucene(data.devices);
  if (luceneDeviceQuery) {
    queryList.push(luceneDeviceQuery);
  }

  // platform
  if (data.platform && data.platform.length > 0 && typeof data.platform.join === 'function') {
    queryList.push(`+platform:(${data.platform.join(' ')})`);
  }

  const NEGATIVE_OPERATORS = [
    QueryBuilder.NOT_MATCHING,
    QueryBuilder.NOT_CONTAINING,
    QueryBuilder.NOT_STARTS_WITH,
    QueryBuilder.NOT_QUERY_PARAM,
    QueryBuilder.DOMAIN_NOT_MATCHING,
  ];

  const WILDCARD_START = [QueryBuilder.CONTAINING, QueryBuilder.NOT_CONTAINING];

  const WILDCARD_END = [
    QueryBuilder.CONTAINING,
    QueryBuilder.NOT_CONTAINING,
    QueryBuilder.STARTS_WITH,
    QueryBuilder.NOT_STARTS_WITH,
  ];

  // filters
  R.forEach((filter: Filter) => {
    var rule = '';
    var countOrLastSeen = filter.operator === QueryBuilder.VALUE_WITHIN ? 'day_last_seen' : 'count';

    switch (filter.option) {
      case QueryBuilder.COMPLETED_EVENT:
        const event = filter.event;
        if (!event) {
          return;
        }
        const eventType = Object.keys(STANDARD_EVENT_NAMES).includes(event) ? 'standard_event' : 'custom_event';
        if (doesFilterIncludeZero(filter)) {
          rule = `+(${eventType}.${escape(event)}.count:${buildRangeForFilter(filter)} (*:* -${eventType}.${escape(
            event
          )}.count:[* TO *]))`;
        } else {
          rule = `+${eventType}.${escape(event)}.${countOrLastSeen}:${buildRangeForFilter(filter)}`;
        }
        break;
      case QueryBuilder.REFERRER:
      case QueryBuilder.INITIAL_REFERRER:
        if (!filter.url && filter.keyValueMatchType !== QueryBuilder.ANY) {
          return;
        }
        const referrerType = filter.option === QueryBuilder.REFERRER ? 'referrer' : 'initial_referrer';

        var selector = '';
        var subSelector = '';
        var keyValueMatchType = '';
        // example query param rule: +referrer.query.product:(abc)
        if (filter.matching === QueryBuilder.QUERY_PARAM || filter.matching === QueryBuilder.NOT_QUERY_PARAM) {
          selector = '.query.';
          subSelector = filter.subSelector || '';
          keyValueMatchType = filter.keyValueMatchType || '';
        } else if (filter.matching === QueryBuilder.CONTAINING || filter.matching === QueryBuilder.NOT_CONTAINING) {
          selector = '.path_contains';
          subSelector = '';
          keyValueMatchType = filter.keyValueMatchType || '';
        } else if (filter.matching == QueryBuilder.DOMAIN_MATCHING || filter.matching == QueryBuilder.DOMAIN_NOT_MATCHING) {
          selector = '.host';
          subSelector = '';
          keyValueMatchType = filter.keyValueMatchType || '';
        }

        var operator =
          R.contains(filter.matching, NEGATIVE_OPERATORS) || R.contains(keyValueMatchType, NEGATIVE_OPERATORS) ? '-' : '+';

        var useLeadingWildcardForContains =
          (R.contains(filter.matching, WILDCARD_START) || R.contains(keyValueMatchType, WILDCARD_START)) &&
          selector !== '.path_contains';

        var wildcardStart = useLeadingWildcardForContains ? '*' : '';

        var wildcardEnd =
          R.contains(filter.matching, WILDCARD_END) || R.contains(keyValueMatchType, WILDCARD_END) ? '*' : '';

        // url rule always starts with: (example: +initial_referrer)
        rule = `${operator}${referrerType}${selector}${subSelector}`;
        const filterUrl = filter.url;
        if (keyValueMatchType === QueryBuilder.ANY || !filterUrl) {
          rule += `:(*)`;
        } else if (Array.isArray(filterUrl) && filterUrl.length) {
          var escapedUrlArray = filterUrl.map((url) => escape(url)).filter((url) => !!url);
          rule += `:(${wildcardStart}`;
          rule += escapedUrlArray.join(`${wildcardEnd} ${wildcardStart}`);
          rule += `${wildcardEnd})`;
        } else {
          // legacy. Url filters are created in Array format
          var escapedUrlFilter = escape(filterUrl);
          rule += `:${wildcardStart}${escapedUrlFilter}${wildcardEnd}`;
        }

        break;
      case QueryBuilder.METADATA:
        // Not a valid filter if it is missing metadata key and values
        // can omit the value if match type is ANY
        if (
          !filter.metadata ||
          !filter.metadata.key ||
          (filter.keyValueMatchType !== QueryBuilder.ANY && !filter.metadata.values)
        ) {
          return;
        }
        const metadataKey = filter.metadata.key || '';
        const metadataValues = filter.metadata.values || [];

        var operator = R.contains(filter.keyValueMatchType, NEGATIVE_OPERATORS) ? '-' : '+';
        var wildcardStart = R.contains(filter.keyValueMatchType, WILDCARD_START) ? '*' : '';
        var wildcardEnd = R.contains(filter.keyValueMatchType, WILDCARD_END) ? '*' : '';

        rule = `${operator}${operator === '-' ? '(' : ''}metadata.hosted_deeplink_data.${metadataKey}:`;

        if (filter.keyValueMatchType === QueryBuilder.ANY) {
          rule += `(*)`;
        } else if (Array.isArray(metadataValues) && metadataValues.length) {
          const escapedMetadataValues = metadataValues.map((v) => escape(escapeOperators(v))).filter((v) => !!v);
          rule += `(${wildcardStart}`;
          rule += escapedMetadataValues.join(`${wildcardEnd} ${wildcardStart}`);
          rule += `${wildcardEnd})`;
        }

        if (operator === '-') {
          rule += ` (*:* -metadata.hosted_deeplink_data.${metadataKey}:(*)))`;
        }
        break;

      case QueryBuilder.VISITED_SITE:
        if (doesFilterIncludeZero(filter)) {
          rule = `+(standard_event.${escape('WEB_SESSION_START')}.count:${buildRangeForFilter(
            filter
          )} (*:* -standard_event.${escape('WEB_SESSION_START')}.count:[* TO *]))`;
        } else {
          rule = `+standard_event.${escape('WEB_SESSION_START')}.${countOrLastSeen}:${buildRangeForFilter(filter)}`;
        }
        break;
      case QueryBuilder.VISITED_APP:
        if (doesFilterIncludeZero(filter)) {
          rule = `+(standard_event.OPEN.count:${buildRangeForFilter(filter)} (*:* -standard_event.OPEN.count:[* TO *]))`;
        } else {
          rule = `+standard_event.OPEN.${countOrLastSeen}:${buildRangeForFilter(filter)}`;
        }
        break;
      case QueryBuilder.EMAIL:
        if (doesFilterIncludeZero(filter)) {
          rule = `+(clicked_email.count:${buildRangeForFilter(filter)} (*:* -clicked_email.count:[* TO *]))`;
        } else {
          rule = `+clicked_email.${countOrLastSeen}:${buildRangeForFilter(filter)}`;
        }
        break;
      case QueryBuilder.ADS:
        if (doesFilterIncludeZero(filter)) {
          rule = `+(clicked_ads.count:${buildRangeForFilter(filter)} (*:* -clicked_ads.count:[* TO *]))`;
        } else {
          rule = `+clicked_ads.${countOrLastSeen}:${buildRangeForFilter(filter)}`;
        }
        break;
      case QueryBuilder.HAS_APP:
        rule = `+has_app:${filter.has_app}`;
        break;
      case QueryBuilder.REGION:
        if (filter.regions) {
          const operator = filter.matching === QueryBuilder.NOT_MATCHING ? '-' : '+';
          var query = `${operator}(`;
          for (var i = 0; i < filter.regions.length; i++) {
            var regionQuery = `(+country:(${filter.regions[i].country_iso_code})`;
            if (filter.regions[i].subdivision_1_iso_code) {
              regionQuery += ` +subdivision:(${escapeOperators(filter.regions[i].subdivision_1_iso_code)})`;
            }
            if (filter.regions[i].city_name) {
              regionQuery += ` +city:("${filter.regions[i].city_name}")`;
            }
            query += regionQuery + ')';
            // Add space in expectation of another query part
            if (i < filter.regions.length - 1) {
              query += ' ';
            }
          }
          query += ')';
          rule = query;
        }
        break;
      case QueryBuilder.JOURNEY_INTERACTION:
        const rangeIsNewFilterDefault = R.equals(filter.range, [1, 2]);
        if (!filter.range[0] || rangeIsNewFilterDefault) {
          filter.range[0] = INTERACTION_PERIOD_DEFAULT;
        }
        filter.operator = QueryBuilder.VALUE_LESS_THAN;
        rule = `+journey_interaction.${filter.interaction || INTERACTION_TYPE_DEFAULT}.${
          filter.journeyIdForInteraction
        }:${buildRangeForFilter(filter)}`;

        // any time a Journey interaction filter is added we need to ensure
        // the 'dismiss' event is part of the event query
        if (data.event && Array.isArray(data.event) && !R.contains('dismiss', data.event)) {
          data.event.push('dismiss');
        }
        break;
      // VIEW_DISMISS set in View Editor page, not a filter on Audience Editor
      case QueryBuilder.VIEW_DISMISS:
        rule = `-view_dismiss.${filter.viewId}:${buildRangeForFilter(filter)}`;
        break;
      default:
        return;
    }
    queryList.push(rule);
  }, data.filters);

  // event
  if (data.event && data.event.length > 0 && typeof data.event.join === 'function') {
    queryList.push(`+event:(${data.event.join(' ')})`);
  }

  if (queryList.length === 0) {
    return '*';
  }
  return queryList.join(' ');
}

function determineMatchType(operator, wildcardStart, wildcardEnd) {
  if (operator === '+') {
    if (!wildcardStart && !wildcardEnd) {
      return QueryBuilder.EXACTLY_MATCHING;
    }
    if (!wildcardStart && wildcardEnd) {
      return QueryBuilder.STARTS_WITH;
    }
    if (wildcardStart && wildcardEnd) {
      return QueryBuilder.CONTAINING;
    }
  }
  if (operator === '-') {
    if (!wildcardStart && !wildcardEnd) {
      return QueryBuilder.NOT_MATCHING;
    }
    if (!wildcardStart && wildcardEnd) {
      return QueryBuilder.NOT_STARTS_WITH;
    }
    if (wildcardStart && wildcardEnd) {
      return QueryBuilder.NOT_CONTAINING;
    }
  }
}

function determineEventOption(event) {
  switch (event) {
    case 'WEB_SESSION_START':
      return QueryBuilder.VISITED_SITE;
    case 'OPEN':
      return QueryBuilder.VISITED_APP;
    case 'email':
      return QueryBuilder.EMAIL;
    case 'ads':
      return QueryBuilder.ADS;
    default:
      return QueryBuilder.COMPLETED_EVENT;
  }
}

// parse lucene query string into object usable by the UI
function extractData(query: string): BuilderData {
  var data = QueryBuilder.createEmptyData();

  // devices
  const devices = convertLuceneToDevices(query);
  if (devices) {
    data.devices = devices;
  }

  // extract event
  // event:(web app)
  var eventRegex = /\+\(?event:\(([^\)]*)\)\)?/g;
  var eventRegexShort = /\+\(?event:(\w+)\)?/g;
  var eventMatch = eventRegex.exec(query);
  if (!eventMatch) {
    eventMatch = eventRegexShort.exec(query);
  }
  if (eventMatch !== null && eventMatch.length >= 2) {
    var eventList = eventMatch[1].split(' ');
    data.event = eventList;
  }

  // extract platform
  // platform:(web amp)
  var platformRegex = /\+\(?platform:\(([^\)]*)\)\)?/g;
  var platformRegexShort = /\+\(?platform:(\w+)\)?/g;
  var platformMatch = platformRegex.exec(query);
  if (!platformMatch) {
    platformMatch = platformRegexShort.exec(query);
  }
  if (platformMatch !== null && platformMatch.length >= 2) {
    var platformList = platformMatch[1].split(' ');
    data.platform = platformList;
  }

  // Matches a full list of regions and the occur (+ or -) for a group of regions. Eg:
  // +((+city:("Palo Alto") +subdivision:(CA) +country:(US)) (+city:("Redwood City") +subdivision:(CA) +country:(US)))
  var regionOccurRegex = /([\-\+])?\(?(\(?([\+\-](country|city|subdivision):\("?([^\)"]+)"?\)\s?)+\)?\s?)+\)?/g;
  // Matches a single region in a group of regions with the same occur (+ or -). Eg, for the region group above, this would have two matches:
  // (+city:("Palo Alto") +subdivision:(CA) +country:(US))
  // and
  // (+city:("Redwood City") +subdivision:(CA) +country:(US))
  var regionRegex = /\(?([\-\+](country|city|subdivision):\(([^\)]*)\)\s?)+\)?/g;
  // Matches a single field in a region. Eg, above region would have three matches:
  // +city:("Palo Alto")
  // +subdivision:(CA)
  // +country:(US)
  var regionPartRegex = /[\+\-](country|city|subdivision):\("?([^\)"]+)"?\)/g;
  var parseRegions = (regionQuery) => {
    var parts;
    var countries = [];
    var region = {};
    while ((parts = regionPartRegex.exec(regionQuery))) {
      // Map to a region object. Eg: +subdivision:(CA) +country:(US) => { "subdivision_1_iso_code": "CA", "country_iso_code": "US" }
      switch (parts[1]) {
        case 'city':
          region.city_name = parts[2];
          break;
        case 'subdivision':
          region.subdivision_1_iso_code = parts[2];
          break;
        case 'country':
          var multi = parts[2].split(' ').filter((c) => /^(\w*)$/.test(c));
          // Legacy region filters could have multiple countries specified in the same field, eg +country:(AB CD EF)
          // If there are multiple countries, it must be a legacy filter, so
          // These are pulled out into an array and returned instead of the region
          if (multi && multi.length > 1) {
            multi.map((country) => {
              countries.push({ country_iso_code: country });
            });
          } else {
            region.country_iso_code = parts[2];
          }
          break;
        default:
          region[parts[1]] = parts[2];
          break;
      }
    }
    return countries.length ? countries : [region];
  };

  // TODO: come up with something better
  function guid() {
    function s4() {
      return Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1);
    }
    return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4();
  }

  // extract list of geo regions
  // eg, +((+city:("Palo alto") +subdivision:(CA) +country:(US))) => {city_name:"Palo alto", subdivision_1_iso_code:"CA", country_name:"US"}
  var match, filter;
  while ((match = regionOccurRegex.exec(query))) {
    var matching = match[1] == '-' ? QueryBuilder.NOT_MATCHING : QueryBuilder.EXACTLY_MATCHING;
    var region;
    var regions = [];
    while ((region = regionRegex.exec(match[0]))) {
      regions = regions.concat(parseRegions(region[0]));
    }
    filter = {
      id: guid(),
      option: QueryBuilder.REGION,
      regions: regions,
      matching: matching,
      operator: QueryBuilder.VALUE_EQUALS,
      range: [1, 2],
    };
    data.filters.push(filter);
  }

  function discoverRange(filter: Filter, range: string, isTimeFrame: boolean): Filter {
    var match = /\[(.+) TO (.+)\]/.exec(range);

    if (isTimeFrame) {
      filter.operator = QueryBuilder.VALUE_WITHIN;
      filter.range = [parseInt(match[2], 10), parseInt(match[2], 10) + 1];
    } else if (match[1] === match[2]) {
      filter.operator = QueryBuilder.VALUE_EQUALS;
      filter.range = [parseInt(match[1], 10), parseInt(match[2], 10)];
    } else if (match[1] === '*') {
      filter.operator = QueryBuilder.VALUE_LESS_THAN;
      filter.range = [parseInt(match[2], 10), parseInt(match[2], 10) + 1];
    } else if (match[2] === '*') {
      filter.operator = QueryBuilder.VALUE_GREATER_THAN;
      filter.range = [parseInt(match[1], 10), parseInt(match[1], 10) + 1];
    } else {
      filter.operator = QueryBuilder.VALUE_BETWEEN;
      filter.range = [parseInt(match[1], 10), parseInt(match[2], 10)];
    }

    return filter;
  }

  // filters
  var filterMatch;
  const completedEventFilter = /\+\(?(custom|standard)_event\.([^\.]+)\.(count|day_last_seen):(\[([^\]]+)\]|\d+)/g;
  while ((filterMatch = completedEventFilter.exec(query)) !== null) {
    const eventOption = determineEventOption(filterMatch[2]);
    const isTimeFrame = filterMatch[3] === 'count' ? false : true;

    filter = {
      id: guid(),
      option: eventOption,
      event: unescape(filterMatch[2]),
    };

    filter = discoverRange(filter, filterMatch[4], isTimeFrame);
    data.filters.push(filter);
  }

  let emailOrAdsMatch;
  const emailOrAdsFilter = /\+\(?clicked_(ads|email)\.(count|day_last_seen):(\[([^\]]+)\]|\d+)/g;
  while ((emailOrAdsMatch = emailOrAdsFilter.exec(query)) !== null) {
    const eventOption = determineEventOption(emailOrAdsMatch[1]);
    const isTimeFrame = emailOrAdsMatch[2] === 'count' ? false : true;

    filter = {
      id: guid(),
      option: eventOption,
      event: unescape(emailOrAdsMatch[1]),
    };

    filter = discoverRange(filter, emailOrAdsMatch[3], isTimeFrame);
    data.filters.push(filter);
  }

  var referredFromSiteFilter = /(\+|\-)\(?(initial_)?referrer(.query.|.path_contains|.host)?([^:]+)?:(\(?)([^)]+)(\)?)/g;
  while ((filterMatch = referredFromSiteFilter.exec(query)) !== null) {
    // filterMatch Regex Array
    // 1: + or -
    // 2: 'initial_' or '' check if urls are referrer or initial_referrer
    // 3: '.query.' or '' check for query param matching
    // 4: query param key to check
    // 5: ( or '' checks if there are parens for multiple query terms
    // 6: url strings or query param value strings separated by a space
    const [, operator, referrerPrefix, selector, subSelector, parens, urlString] = filterMatch;

    var wildcardStart = urlString.charAt(0) === '*';
    var wildcardEnd = urlString.charAt(urlString.length - 1) === '*';

    var urlList = urlString.split(' ');

    var matchType = determineMatchType(operator, wildcardStart, wildcardEnd);
    var matching = matchType;
    var keyValueMatchType = null;

    if (selector == '.query.') {
      matching = operator == '-' ? QueryBuilder.NOT_QUERY_PARAM : QueryBuilder.QUERY_PARAM;
      if (operator === '-') {
        // If 'without query param',
        // override match type to avoid double negative
        matchType = determineMatchType('+', wildcardStart, wildcardEnd);
      }
      keyValueMatchType = urlList.length === 1 && urlList[0] === '*' ? QueryBuilder.ANY : matchType;
    } else if (selector == '.path_contains') {
      matching = operator == '-' ? QueryBuilder.NOT_CONTAINING : QueryBuilder.CONTAINING;
    } else if (selector == '.host') {
      matching = operator == '-' ? QueryBuilder.DOMAIN_NOT_MATCHING : QueryBuilder.DOMAIN_MATCHING;
    }

    var referrerType = referrerPrefix ? QueryBuilder.INITIAL_REFERRER : QueryBuilder.REFERRER;

    var urlArray =
      parens === '('
        ? urlList
            .filter((v) => !!v)
            .map((url) => {
              if (!url || url === '*') {
                return '';
              }

              // finds the url or term. Ignoring '*' at beginning or end
              var searchTermRE = /(\*?)([^\s\*]+)(\*?)/g;
              var searchTerm = searchTermRE.exec(url);

              return unescape(searchTerm[2]);
            })
        : [unescape(urlString)];

    filter = {
      id: guid(),
      option: referrerType,
      matching: matching,
      keyValueMatchType: keyValueMatchType,
      subSelector: subSelector || null,
      url: urlArray,
      operator: QueryBuilder.VALUE_EQUALS,
      range: [1, 2],
    };

    data.filters.push(filter);
  }

  const metadataFilter = /(\*\:\* )?(\+|\-)\(?metadata\.hosted_deeplink_data\.([^:]+)?:(\(?)([^)]+)(\)?)\)?/g;
  while ((filterMatch = metadataFilter.exec(query)) !== null) {
    // filterMatch Regex Array
    // 1: *:*
    // 2: + or -
    // 3: metadata key
    // 4: ( or '' checks if there are parens for multiple query terms
    // 5: value
    // 6: ) or ''
    const [, excludeExpression, operator, metadataKey, parens, metadataValuesString] = filterMatch;
    if (excludeExpression) {
      continue;
    }

    var wildcardStart = metadataValuesString.charAt(0) === '*';
    var wildcardEnd = metadataValuesString.charAt(metadataValuesString.length - 1) === '*';

    var metadataValueList = metadataValuesString.split(' ');

    var matchType = determineMatchType(operator, wildcardStart, wildcardEnd);
    var matching = matchType;

    const keyValueMatchType = metadataValueList.length === 1 && metadataValueList[0] === '*' ? QueryBuilder.ANY : matchType;

    var metadataValues =
      parens === '('
        ? metadataValueList
            .filter((v) => !!v)
            .map((value) => {
              if (!value || value === '*') {
                return '';
              }

              // finds the value or term. Ignoring '*' at beginning or end
              var searchTermRE = /(\*?)([^\s\*]+)(\*?)/g;
              var searchTerm = searchTermRE.exec(value);

              return unescape(searchTerm[2]);
            })
        : [unescape(metadataValuesString)];

    filter = {
      id: guid(),
      option: QueryBuilder.METADATA,
      matching: null,
      keyValueMatchType: keyValueMatchType,
      subSelector: null,
      url: '',
      operator: QueryBuilder.VALUE_EQUALS,
      range: [1, 2],
      metadata: {
        key: metadataKey,
        values: metadataValues,
      },
    };

    data.filters.push(filter);
  }

  var appIsInstalledFilter = /\+has_app:\(?(true|false)\)?/g;
  while ((filterMatch = appIsInstalledFilter.exec(query)) !== null) {
    filter = {
      id: guid(),
      option: QueryBuilder.HAS_APP,
      has_app: filterMatch[1],
    };
    data.filters.push(filter);
  }

  var journeyInteractionFilter = /\+journey_interaction.(\w+).(\w+):\[\*\sTO\s(\d+|\*)\]/g;
  while ((filterMatch = journeyInteractionFilter.exec(query)) !== null) {
    // filterMatch Regex Array:
    // 0: '+journey_interaction.${interaction}.${journeyIdForInteraction}:[* TO ${dismissPeriod}]'
    // 1: interaction
    // 2: journeyIdForInteraction
    // 3: dismissPeriod
    filter = {
      id: guid(),
      option: QueryBuilder.JOURNEY_INTERACTION,
      operator: QueryBuilder.VALUE_LESS_THAN,
      range: [Number(filterMatch[3])],
      interaction: filterMatch[1],
      journeyIdForInteraction: filterMatch[2],
    };
    data.filters.push(filter);
  }

  // VIEW_DISMISS set in View Editor page, not a filter on Audience Editor
  var viewDismissFilterRegex = /\-view_dismiss.(\d+):\[\*\sTO\s(\d+|\*)\]/g;
  while ((filterMatch = viewDismissFilterRegex.exec(query)) !== null) {
    // filterMatch Regex Array:
    // 0: '-view_dismiss.${viewId}:[* TO ${dismissPeriod}]'
    // 1: viewId
    // 2: dismissPeriod
    let operator = QueryBuilder.VALUE_LESS_THAN;
    let dismissPeriod = Number(filterMatch[2]);
    if (filterMatch[2] === '*') {
      operator = QueryBuilder.NO_BOUNDS;
      dismissPeriod = -1;
    }

    const viewDismissFilter: ViewDismissFilter = {
      id: guid(),
      option: QueryBuilder.VIEW_DISMISS,
      operator,
      range: [dismissPeriod],
      viewId: filterMatch[1],
    };
    data.filters.push(viewDismissFilter);
  }

  return data;
}

function getQuery() {
  return QueryBuilder.buildQuery(this._data);
}

function setQuery(query) {
  this.setData(QueryBuilder.extractData(query));
}

function getData() {
  return R.clone(this._data);
}

// Separate concerns how to access this AMP state by creating centrally reusable getter function, in the event that property keys change.
function isAmpCompatible() {
  const data = R.clone(this._data);
  return data && data.platform && data.platform.indexOf(AMP_KEY) !== -1;
}

function setData(data) {
  this._data = R.clone(data);
}

/*
 * @param {Array} - path ['device', 'ios']
 * @param {string} - data "tablet mobile"
 */
function addDataByPath(path, data) {
  var set = data.split(' ');
  if (set.length > 1) {
    return set.forEach((d) => this.addDataByPath(path, d));
  }
  this._data = this._data || QueryBuilder.createEmptyData();
  const currentValue = R.pathOr([], path, this._data);
  if (currentValue.indexOf(data) === -1) {
    // We are not mutating the value here, but instead resetting it,
    // although this doesn't entirely follow the patterns used elsewhere here.
    // Mutating the value caused both 'ios' and 'android' to update inexplicably.
    const newValue = [...currentValue, data];
    this._data = R.assocPath(path, newValue, this._data);
  }
}

/*
 * @param {Array} - path ['device', 'ios']
 * @param {string} - data "tablet"
 */
function removeDataByPath(path, data) {
  var set = data.split(' ');
  if (set.length > 1) {
    return set.forEach((d) => this.removeDataByPath(path, d));
  }
  this._data = this._data || QueryBuilder.createEmptyData();
  const currentValue = R.pathOr([], path, this._data);
  const index = currentValue.indexOf(data);
  if (index !== -1) {
    // We are not mutating the value here, but instead resetting it,
    // although this doesn't entirely follow the patterns used elsewhere here.
    // Mutating the value caused both 'ios' and 'android' to update inexplicably.
    const newValue = currentValue.slice();
    newValue.splice(index, 1);
    this._data = R.assocPath(path, newValue, this._data);
  }
}

function getRegionLabel(data) {
  if (!data || !data.country_name) {
    return null;
  }
  var label = data.country_name;
  if (data.subdivision_1_name) {
    label = data.subdivision_1_name + ', ' + label;
  }
  if (data.city_name) {
    label = data.city_name + ', ' + label;
  }
  return label;
}

function getRegionIsoLabel(data) {
  if (!data || !data.country_iso_code) {
    return null;
  }
  var label = data.country_iso_code;
  if (data.subdivision_1_iso_code) {
    label = data.subdivision_1_iso_code + ', ' + label;
  }
  if (data.city_name) {
    label = data.city_name + ', ' + label;
  }
  return label;
}

function getRegionKey(data) {
  if (!data || !data.country_iso_code) {
    return null;
  }
  var key = data.country_iso_code;
  if (data.subdivision_1_iso_code) {
    key += data.subdivision_1_iso_code;
  }
  if (data.city_name) {
    key += data.city_name;
  }
  return key;
}

function getFilters() {
  return this._data.filters.slice();
}

function deleteFilter(index) {
  this._data.filters.splice(index, 1);
}

function setFilter(filter, index) {
  if (typeof index === 'undefined') {
    this._data.filters.push(filter);
  } else {
    this._data.filters[index] = filter;
  }
}

const findFilterIndex = (filter: Filter, filters: Array<Filter>): number => {
  // 1. We have the unique id to locate the existing filter
  if (filter.id) {
    return R.findIndex(R.propEq('id', filter.id))(filters);
  }
  // 2. We are updating a filter for a viewId
  if (filter.viewId) {
    return R.findIndex(R.propEq('viewId', filter.viewId))(filters);
  }
  // 3. Otherwise we can update the filter with the same option
  // (assuming we did not in intend to have two filters with the same option
  // except for viewId filters)
  return R.findIndex(R.propEq('option', filter.option))(filters);
};

function findAndUpdateFilter(filter: Filter) {
  const index = findFilterIndex(filter, this._data.filters);
  if (index === -1) {
    this._data.filters.push({ ...filter });
  } else {
    this._data.filters[index] = { ...filter };
  }
}

function findAndDeleteFilter(filter: Filter) {
  const index = findFilterIndex(filter, this._data.filters);
  // only delete the filter if we found it
  if (index !== -1) {
    this._data.filters.splice(index, 1);
  }
}

function setFilters(filters) {
  this._data.filters = filters.slice();
}

// static methods
QueryBuilder.createEmptyData = createEmptyData;
QueryBuilder.buildQuery = buildQuery;
QueryBuilder.extractData = extractData;
QueryBuilder.getRegionKey = getRegionKey;
QueryBuilder.getRegionLabel = getRegionLabel;
QueryBuilder.getRegionIsoLabel = getRegionIsoLabel;

QueryBuilder.prototype = {
  getQuery: getQuery,
  setQuery: setQuery,
  getData: getData,
  setData: setData,
  getFilters: getFilters,
  setFilter: setFilter,
  findAndUpdateFilter: findAndUpdateFilter,
  findAndDeleteFilter: findAndDeleteFilter,
  setFilters: setFilters,
  deleteFilter: deleteFilter,
  addDataByPath: addDataByPath,
  removeDataByPath: removeDataByPath,
  isAmpCompatible: isAmpCompatible,
};

QueryBuilder = R.reduce(
  (object, item) => {
    object[item] = item;
    return object;
  },
  QueryBuilder,
  [...QUERY_BUILDER_TYPES_LIST]
);

export default QueryBuilder;
